import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ShareGroupDialog } from "@/components/groups/ShareGroupDialog";
import { DeleteGroupDialog } from "@/components/groups/DeleteGroupDialog";
import { EditGroupDialog } from "@/components/groups/EditGroupDialog";
import { CreateGroupDialog } from "@/components/groups/CreateGroupDialog";
import { Mail, Share2, Trash2, ExternalLink, Search, Pencil, Plus } from "lucide-react";
import { useSetAtom } from 'jotai';
import { campaignWizardAtom } from '@/lib/atoms';
import { useToast } from "@/hooks/use-toast";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { getInvalidationKeys } from '@/lib/query-keys';
import { format, isValid } from "date-fns";
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import type { Contact } from '@/types/contact';

interface Group {
  id: string;
  name: string;
  contact_count: number;
  workspace_id: string;
  created_at: string;
}

export function GroupsPage() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const setCampaignWizard = useSetAtom(campaignWizardAtom);
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  const fetchGroups = async () => {
    if (!currentWorkspace) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('view_group_counts')
        .select('*')
        .eq('workspace_id', currentWorkspace.id);

      if (error) throw error;

      const formattedGroups = data.map(group => ({
        id: group.group_id,
        name: group.group_name,
        contact_count: group.contact_count,
        workspace_id: group.workspace_id,
        created_at: group.created_at
      }));

      setGroups(formattedGroups);
    } catch (error) {
      console.error('Error fetching groups:', error);
      captureError(error as Error, { feature: 'groups-list-fetch' });
      toast({
        title: "Error",
        description: "Could not fetch groups.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentWorkspace) {
      fetchGroups();
    }
  }, [currentWorkspace]);

  const handleDeleteGroup = async () => {
    if (!selectedGroup) return;

    const originalGroups = [...groups];
    setIsDeleting(true);
    setGroups(groups.filter(g => g.id !== selectedGroup.id));

    try {
      const { error } = await supabase
        .from('groups')
        .delete()
        .eq('id', selectedGroup.id);

      if (error) throw error;

      toast({
        description: "Group deleted successfully.",
      });

      // Invalidate groups queries to refresh nav
      const invalidationKeys = getInvalidationKeys.afterGroupMutation(currentWorkspace?.id);
      await Promise.all(
        invalidationKeys.map(key => queryClient.invalidateQueries({ queryKey: key }))
      );

      setShowDeleteDialog(false);
      setSelectedGroup(null);
    } catch (error) {
      setGroups(originalGroups);
      captureError(error as Error, { feature: 'groups-list-delete-group' });
      toast({
        title: "Error",
        description: "Could not delete group.",
        variant: "destructive",
      });
      console.error('Error deleting group:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEmailGroup = async (groupId: string) => {
    try {
      const { data, error } = await supabase.rpc('get_contacts_by_group_id', {
        p_group_id: groupId
      });

      if (error) throw error;

      const validContacts = data.filter((contact: Contact) => contact.primary_email);

      if (validContacts.length === 0) {
        toast({
          title: "No contacts to email",
          description: "This group has no contacts with a valid email address.",
          variant: "default",
        });
        return;
      }

      setCampaignWizard({ open: true, selectedContacts: validContacts });
    } catch (error) {
      captureError(error as Error, { feature: 'groups-list-create-group' });
      toast({
        title: "Error",
        description: "Could not fetch contacts for this group.",
        variant: "destructive",
      });
      console.error('Error fetching group contacts:', error);
    }
  };

  const handleGroupClick = (group: Group) => {
    navigate(`/contacts?groupName=${encodeURIComponent(group.name)}`);
  };

  // Filter groups based on search term
  const filteredGroups = groups.filter(group =>
    group.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!currentWorkspace) {
    return (
      <div className="h-full p-6">
        <div className="text-center py-8 text-muted-foreground">
          No workspace selected
        </div>
      </div>
    );
  }

  return (
    <div className="h-full p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold tracking-tight">Groups</h1>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => setShowCreateDialog(true)}
            title="Create group"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="relative max-w-md w-full">
          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search groups..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-8">Loading groups...</div>
      ) : filteredGroups.length > 0 ? (
        <div className="space-y-2">
          {filteredGroups.map((group) => (
            <div
              key={group.name}
              className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-center gap-6 flex-1">
                <button
                  onClick={() => handleGroupClick(group)}
                  className="font-medium hover:underline text-left"
                >
                  {group.name}
                </button>
                <span className="text-sm text-muted-foreground">
                  {group.contact_count} contacts
                </span>
                {group.created_at && (
                  <span className="text-sm text-muted-foreground">
                    {isValid(new Date(group.created_at)) 
                      ? format(new Date(group.created_at), 'MMM d, yyyy')
                      : 'Invalid date'}
                  </span>
                )}
              </div>
              <TooltipProvider>
                <div className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleGroupClick(group)}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View contacts in this group</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEmailGroup(group.id)}
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Send email to all contacts in this group</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedGroup(group);
                          setShowShareDialog(true);
                        }}
                      >
                        <Share2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Share this group with team members</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedGroup(group);
                          setShowEditDialog(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Edit group name</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        onClick={() => {
                          setSelectedGroup(group);
                          setShowDeleteDialog(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Delete group (keeps contacts)</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </TooltipProvider>
            </div>
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <p className="text-muted-foreground">No groups yet.</p>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create group
          </Button>
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          No groups found matching your search.
        </div>
      )}

      {selectedGroup && (
        <ShareGroupDialog
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          groupId={selectedGroup.id}
          groupName={selectedGroup.name}
        />
      )}

      {selectedGroup && (
        <DeleteGroupDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          groupName={selectedGroup.name}
          contactCount={selectedGroup.contact_count}
          onConfirm={handleDeleteGroup}
          isDeleting={isDeleting}
        />
      )}

      {selectedGroup && (
        <EditGroupDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          groupId={selectedGroup.id}
          currentName={selectedGroup.name}
          onSuccess={fetchGroups}
        />
      )}

      <CreateGroupDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSuccess={fetchGroups}
      />
    </div>
  );
}